import type en from "./en";

/** Spanish messages — typed against the English schema so a missing key is a build error. */
type MessageSchema = typeof en;

const es: MessageSchema = {
  common: {
    save: "Guardar",
    saving: "Guardando…",
    cancel: "Cancelar",
    confirm: "Confirmar",
    delete: "Eliminar",
    deleting: "Eliminando…",
    edit: "Editar",
    remove: "Quitar",
    removing: "Quitando…",
    previous: "Anterior",
    next: "Siguiente",
    close: "Cerrar",
    loading: "Cargando…",
    unexpectedError: "Se ha producido un error inesperado.",
    you: "Tú",
  },

  nav: {
    sidebarTitle: "Menú de navegación",
    sidebarDescription: "Muévete entre las secciones de la aplicación.",
    planning: "Planificación",
    team: "Equipo",
    admin: "Admin",
    account: "Cuenta",
    logout: "Cerrar sesión",
    language: "Idioma",
    spanish: "Español",
    english: "English",
  },

  theme: {
    toLight: "Cambiar a modo claro",
    toDark: "Cambiar a modo oscuro",
  },

  routeTitles: {
    admin: "Admin",
    account: "Cuenta",
    team: "Equipo",
    planning: "Planificación",
    workoutNew: "Nuevo entrenamiento",
    workoutDetail: "Entrenamiento",
    zenith: "Zenith",
  },

  auth: {
    brandTagline: "Entrena en tu mejor momento",
    login: {
      title: "Bienvenido de nuevo",
      email: "Email",
      password: "Contraseña",
      forgotPassword: "¿Has olvidado tu contraseña?",
      submit: "Iniciar sesión",
      submitting: "Iniciando sesión...",
      terms: "Al continuar, aceptas nuestros {terms} y nuestra {privacy}.",
      termsOfService: "Términos del servicio",
      privacyPolicy: "Política de privacidad",
      failedTitle: "Error al iniciar sesión",
    },
    register: {
      title: "Crear una cuenta",
      inviteOnly:
        "Zenith es solo por invitación. Tras activar tu cuenta, un responsable de equipo tiene que añadirte a un equipo.",
      checkEmailTitle: "Revisa tu correo",
      checkEmailBody: "Hemos enviado un enlace de activación a {email}.",
      checkEmailFollow:
        "Sigue el enlace del correo para establecer tu contraseña y activar tu cuenta.",
      email: "Email",
      submit: "Continuar con el email",
      submitting: "Enviando...",
      hasAccount: "¿Ya tienes una cuenta?",
      signIn: "Inicia sesión",
      failedTitle: "Error al registrarse",
    },
    forgotPassword: {
      title: "¿Has olvidado tu contraseña?",
      checkEmailTitle: "Revisa tu correo",
      checkEmailBody:
        "Si existe una cuenta para {email}, te hemos enviado un enlace de restablecimiento.",
      email: "Email",
      submit: "Enviar enlace de restablecimiento",
      submitting: "Enviando...",
      remembered: "¿Recuerdas tu contraseña?",
      signIn: "Inicia sesión",
      failedTitle: "Error en la solicitud",
    },
    setPassword: {
      invalidTitle: "Enlace no válido o caducado",
      invalidBody: "Este enlace no es válido o ha caducado.",
      requestNewLink: "Solicitar un nuevo enlace",
      welcomeTitle: "Bienvenido a Zenith",
      setTitle: "Establece tu contraseña",
      welcomeDescription: "Establece tu contraseña para activar tu cuenta.",
      newPassword: "Nueva contraseña",
      confirmPassword: "Confirmar contraseña",
      passwordTooShort: "La contraseña debe tener al menos 8 caracteres.",
      passwordsDontMatch: "Las contraseñas no coinciden.",
      submit: "Establecer contraseña",
      submitting: "Estableciendo contraseña...",
      successToast: "Contraseña establecida correctamente. Ya puedes iniciar sesión.",
      failedTitle: "No se pudo establecer la contraseña",
      failedFallback: "Puede que este enlace haya caducado. Solicita uno nuevo.",
    },
  },

  account: {
    title: "Cuenta",
    loadingProfile: "Cargando perfil…",
    profileTitle: "Perfil",
    profileDescription: "Actualiza tu nombre visible. Tu email no se puede cambiar aquí.",
    email: "Email",
    firstName: "Nombre",
    lastName: "Apellidos",
    save: "Guardar cambios",
    saving: "Guardando...",
    updatedToast: "Perfil actualizado.",
    failedTitle: "Error al actualizar",
  },

  admin: {
    title: "Admin",
    subtitle: "Gestión de equipos de toda la plataforma",
    createTeamTitle: "Crear equipo",
    createTeamDescription:
      "Solo los administradores de la plataforma pueden crear equipos. Se invita al responsable por email.",
    teamName: "Nombre del equipo",
    teamNamePlaceholder: "Club Atletismo Norte",
    managerEmailPlaceholder: "responsable{'@'}club.es",
    managerEmailHint:
      "Si ya tiene cuenta se reutiliza; si es nuevo, recibe un email de activación.",
    lastInvited: "Último equipo creado: {team} — invitación enviada a {email}.",
    errors: {
      nameRequired: "Indica el nombre del equipo.",
      emailRequired: "Indica el email del responsable.",
      emailInvalid: "Introduce un email válido, por ejemplo nombre{'@'}club.es.",
    },
    managerEmail: "Email del responsable",
    demo: "Configurar como equipo de demostración",
    demoHint:
      "Añade dos semanas de entrenamientos de ejemplo y da al responsable el rol de entrenador para que los vea.",
    createSubmit: "Crear equipo",
    creating: "Creando…",
    createdToast: "Equipo creado — invitación enviada a {email}",
    createFailedTitle: "Error al crear",
    teamsTitle: "Equipos",
    teamsDescription: "Todos los equipos de la plataforma.",
    loadFailed: "No se pudieron cargar los equipos.",
    empty: "Todavía no hay equipos — crea uno arriba.",
    memberCount: "{n} miembro | {n} miembros",
  },

  team: {
    managementSubtitle: "Gestión del equipo",
    accessError: "No tienes acceso para gestionar este equipo, o no se pudo cargar.",
    managerRequiredTitle: "Se requiere acceso de responsable",
    managerRequiredBody: "No gestionas este equipo.",
    membersTitle: "Miembros",
    inviteMember: "Invitar miembro",
    inviteEmail: "Dirección de email",
    roles: "Roles",
    role: {
      manager: "Responsable",
      coach: "Entrenador/a",
      athlete: "Deportista",
    },
    sendInvite: "Enviar invitación",
    inviting: "Invitando…",
    inviteSentToast: "Invitación enviada.",
    inviteFailedTitle: "Error al invitar",
    searchPlaceholder: "Buscar por nombre o email…",
    searchLabel: "Buscar miembros",
    clearSearch: "Borrar búsqueda",
    filterAll: "Todos",
    memberCount: "{n} miembro | {n} miembros",
    loadMembersFailed: "No se pudieron cargar los miembros.",
    noRoles: "Sin roles",
    removedToast: "{name} quitado/a del equipo.",
    removeFailedTitle: "Error al quitar",
    rolesButton: "Roles",
    rolesUpdatedToast: "Roles actualizados para {name}.",
    rolesUpdateFailedTitle: "Error al actualizar",
    invitePlaceholder: "nombre{'@'}ejemplo.com",
    roleHelp: {
      manager: "Administra el equipo. Sin acceso a la planificación.",
      coach: "Planifica entrenamientos y gestiona grupos.",
      athlete: "Ve la planificación de todo el equipo.",
    },
    noRolesHelp: "No ve la planificación hasta que se le asigne un rol.",
    editRolesFor: "Editar roles de {name}",
    onlyManagerHint: "Eres el único responsable. Añade otro antes.",
    demoteTitle: "¿Quitarte tu propio rol de responsable?",
    demoteBody: "Perderás el acceso a esta página. Otro responsable tendría que devolvértelo.",
    removeMemberFor: "Quitar a {name}",
    removeConfirmTitle: "¿Quitar a {name} del equipo?",
    removeConfirmBody: "Perderá el acceso a la planificación.",
    inviteRolesRequired: "Selecciona al menos un rol.",
    sectionContinued: "continuación",
    retry: "Reintentar",
    pagination: "Páginas",
    emptyNoFilter: "Todavía no hay miembros — invita a alguien para empezar.",
    emptyFiltered: "Ningún miembro coincide con tu búsqueda o filtro.",
    prev: "Anterior",
    next: "Siguiente",
    pageOf: "Página {page} de {total}",
  },

  teamSwitcher: {
    teamsLabel: "Equipos",
    noTeam: "Sin equipo",
  },

  noTeam: {
    title: "Todavía sin equipo",
    description:
      "No perteneces a ningún equipo. Pide a un responsable de equipo que te invite y vuelve a iniciar sesión.",
    shareEmail: "Dale este correo a tu responsable de equipo: {email}",
    editProfile: "Editar perfil",
    logout: "Cerrar sesión",
  },

  selectTeam: {
    title: "Selecciona un equipo",
    description: "Elige en qué equipo quieres trabajar.",
    loading: "Cargando equipos…",
  },

  groupManager: {
    title: "Grupos",
    description:
      "Organiza los entrenamientos del equipo en grupos, por ejemplo por categoría o nivel.",
    save: "Guardar",
    cancelRename: "Cancelar cambio de nombre",
    createdToast: "Grupo creado.",
    createFailedTitle: "Error al crear",
    renamedToast: "Grupo renombrado.",
    renameFailedTitle: "Error al renombrar",
    removedToast: "Grupo eliminado.",
    removeFailedTitle: "Error al eliminar",
    removeConfirm: "¿Eliminar este grupo? Debe estar vacío.",
    onlyGroupHint: "Único grupo — el equipo necesita al menos uno",
    rename: "Renombrar",
    removeDisabledTitle: "Un equipo debe tener al menos un grupo",
    removeDisabledLabel: "Eliminar grupo — deshabilitado, el equipo necesita al menos un grupo",
    removeLabel: "Eliminar grupo",
    remove: "Eliminar",
    createLabel: "Crear grupo",
    createPlaceholder: "Nombre del grupo",
    addGroup: "Añadir grupo",
    creating: "Creando…",
  },

  // Week view (group lanes) — owned by the WeekView rebuild.
  weekView: {
    groupColumnLabel: "Grupo",
    sessionCount: "{n} sesión | {n} sesiones",
    noGroup: "Sin grupo",
    add: "Añadir",
    emptyWeek: "Sin sesiones esta semana.",
    emptyLane: "Sin sesiones esta semana para {group}. Prueba con otro grupo.",
    noGroupsMessage:
      "Este equipo todavía no tiene grupos. Los entrenadores pueden añadir uno desde «Gestionar grupos».",
  },

  // Day view (day rail + day detail) — owned by the DayView build.
  dayView: {
    share: "Compartir",
    sharePreparing: "Preparando…",
    shareCopied: "Entrenamiento copiado como texto",
    shareDownloaded: "Descargado",
    shareFailed: "No se pudo compartir el entrenamiento",
    today: "hoy",
    restDayForGroup: "Día de descanso para {group}.",
  },

  planning: {
    loadError: "No se pudo cargar el calendario.",
    retry: "Reintentar",
    dayLabel: "Día",
    viewSwitcher: "Vista",
    monthLabel: "Mes",
    weekLabel: "Semana",
    dayPrev: "Semana anterior",
    dayNext: "Semana siguiente",
    weekPrev: "Semana anterior",
    weekNext: "Semana siguiente",
    monthPrev: "Mes anterior",
    monthNext: "Mes siguiente",
    previousWeek: "Semana anterior (← / j)",
    nextWeek: "Semana siguiente (→ / k)",
    today: "Hoy (t)",
    todayShort: "Hoy",
    allGroups: "Todos los grupos",
    manageGroups: "Gestionar grupos",
    copyWeek: "Copiar esta semana a…",
    copyWeekTitle: "Copiar esta semana",
    copyWeekDescription:
      "Copia los entrenamientos de la semana del {from} a otra semana. Elige cualquier día de la semana de destino.",
    copyWeekInto: "Se copia a la semana del {to}. Lo que ya esté planificado se mantiene.",
    copyWeekSameWeek: "Elige una semana distinta a la de origen.",
    copyWeekTarget: "Semana de destino",
    copyWeekAction: "Copiar semana",
    copyWeekDone: "Copiados {count} entrenamientos",
    copyWeekEmpty: "No había entrenamientos que copiar",
    copyWeekFailed: "No se pudo copiar la semana",
    newWorkout: "Nuevo entrenamiento (n)",
    newWorkoutShort: "Nuevo entrenamiento",
    restDay: "Día de descanso",
    emptyWeekTitle: "No hay entrenamientos programados esta semana",
    emptyWeekBody:
      "Empieza a construir tu semana de entrenamiento — añade el primero en cualquier día.",
    emptyWeekBodyMobile: "Empieza a construir tu semana añadiendo el primer entrenamiento.",
    emptyMonthTitle: "No hay entrenamientos programados este mes",
    emptyMonthBody: "Haz clic en cualquier día para abrirlo y empezar a planificar.",
    emptyMonthAgenda: "No hay entrenamientos programados este mes.",
    addWorkout: "Añadir entrenamiento",
    newWorkoutOn: "Nuevo entrenamiento el {date}",
    addWorkoutOnDay: "Añadir entrenamiento el {day}",
    addWorkoutOnDayDate: "Añadir entrenamiento el {day} {date}",
    jumpToWeek: "Abrir {label}",
    jumpToWeekAria: "Abrir {label}",
    weekTotal: "Semana",
    shortcuts: "Atajos de teclado",
    more: "Más opciones",
    moreWorkouts: "{n} entrenamientos más el {label}",
    moreWorkoutsShort: "+{n} más",
    stepCount: "{n} paso | {n} pasos",
  },

  workoutEditor: {
    backToPlanning: "Volver a planificación",
    more: "Más",
    edit: "Editar",
    duplicate: "Duplicar",
    duplicateOf: "Copia de {title}",
    confirmDelete: "Confirmar eliminar",
    notFound: "Entrenamiento no encontrado o no tienes acceso.",
    addTitle: "Añadir título",
    titleAriaLabel: "Título del entrenamiento",
    disciplineAriaLabel: "Disciplina",
    disciplinePlaceholder: "Elige disciplina",
    disciplineRequired: "Elige una disciplina.",
    dateAriaLabel: "Fecha",
    groupAriaLabel: "Grupo",
    groupPlaceholder: "Selecciona un grupo…",
    groupRequired: "Selecciona un grupo.",
    notes: "Notas",
    addNotes: "Añadir notas",
    discard: "Descartar",
    saveChanges: "Guardar cambios",
    leaveConfirm: "Tienes cambios sin guardar. ¿Salir sin guardar?",
    unsaved: "Cambios sin guardar",
    disciplineLabel: "Disciplina",
    dateLabel: "Fecha",
    groupLabel: "Grupo",
    createWorkout: "Crear entrenamiento",
    saving: "Guardando…",
    updatedToast: "Entrenamiento actualizado.",
    createdToast: "Entrenamiento creado.",
    updateFailedTitle: "Error al actualizar",
    createFailedTitle: "Error al crear",
    deletedToast: "Entrenamiento eliminado.",
    deleteFailedTitle: "Error al eliminar",
  },

  planText: {
    label: "Sesión",
    checking: "Revisando el texto…",
    howTitle: "Cómo escribirlo (un paso por línea)",
    help: {
      time: "Tiempo: minutos ('), segundos (\"), horas (h)",
      distance: "Distancia: metros, kilómetros, millas",
      type: "Tipo de paso (sin tipo, es un paso principal)",
      rpe: "RPE de 1 a 10",
      repeat:
        "Repetición: N x ( pasos separados por / o por líneas ). Sin paréntesis repite el resto de la línea",
      note: "Cualquier palabra suelta o // texto queda como nota",
    },
    typeWords: "cal  rec  desc  enfriamiento",
    errorCount: "Sin errores | 1 error | {n} errores",
    shortcuts:
      "{mod}+D duplica la línea o repetición · {alt}+↑/↓ la mueve · {mod}+Z deshace · {mod}+S guarda.",
    line: "Línea {n}",
    fixToSave: "Corrige los errores del texto para guardar.",
    tab: "Texto",
    stale: "Mostrando los últimos pasos válidos. Corrige el texto para actualizarlos.",
    viewLabel: "Pasos o texto",
    error: {
      unknownToken:
        "«{text}» no se reconoce. Usa tiempo (15'), distancia (400m), un tipo (warm, rec, rest, cool) o {'@'}RPE7.",
      duplicateVolume: "«{text}»: este paso ya tiene volumen.",
      duplicateType: "«{text}»: este paso ya tiene tipo.",
      duplicateRpe: "«{text}»: este paso ya tiene RPE.",
      badVolume: "«{text}»: el volumen debe ser mayor que 0 y razonable (máx. 24 h o 1000 km).",
      badRpe: "«{text}»: el RPE es un solo número de 1 a 10 (ej. {'@'}RPE7).",
      badReps: "«{text}»: las repeticiones van de 1 a 999.",
      emptyGroup: "La repetición no tiene pasos: escribe N x ( … ) o N x paso.",
      unclosed: "Falta cerrar el paréntesis.",
      unexpectedClose: "Sobra este paréntesis.",
      groupTrailing: "«{text}»: una repetición no admite RPE, tipo ni nota. Ponlo en cada paso.",
    },
  },
  planEditor: {
    empty: "Sin pasos todavía.",
    heading: "Pasos",
    guide:
      "Construye la sesión paso a paso. Pulsa un valor (tiempo, distancia, RPE) para cambiarlo.",
    guideTouch: "Construye la sesión paso a paso. Toca un paso para editarlo.",
    addStep: "Añadir paso",
    addRepeat: "Añadir repetición",
    addStepToRepeatN: "Añadir paso a la repetición de {n}×",
    addRepeatToRepeat: "Añadir una repetición dentro",
    kindMeters: "Distancia · m",
    kindKm: "Distancia · km",
    kindMiles: "Distancia · mi",
    addStepToRepeat: "Añadir paso a la repetición",
    addStepBelow: "Añadir paso debajo",
    phase: {
      warmup: "Calentamiento",
      main: "Parte principal",
      cooldown: "Vuelta a la calma",
    },
    workTypeLabel: "Tipo de trabajo",
    volumeLabel: "Volumen del paso",
    volumeKindLabel: "Tipo de volumen",
    timeMode: "Tiempo",
    hoursLabel: "Horas",
    minutesLabel: "Minutos",
    secondsLabel: "Segundos",
    distanceValueLabel: "Distancia",
    repsLabel: "Repeticiones",
    rowMenuStep: "Opciones del paso",
    rowMenuRepeat: "Opciones de la repetición",
    moveUp: "Subir",
    moveDown: "Bajar",
    moveIntoPrevRepeat: "Meter en la repetición de arriba",
    moveIntoNextRepeat: "Meter en la repetición de abajo",
    moveOutOfRepeat: "Sacar de la repetición",
    repeatThis: "Repetir",
    unwrapRepeat: "Deshacer repetición",
    removeStep: "Eliminar",
    removeRepeat: "Eliminar repetición",
    duplicate: "Duplicar",
    stepRemoved: "Paso eliminado",
    repeatRemoved: "Repetición eliminada",
    undo: "Deshacer",
    notesPlaceholder: "Añadir nota",
    notesLabel: "Notas del paso",
    noRpe: "Sin RPE",
    editStep: "Editar paso",
    editStepDescription: "Cambia el tipo, el volumen, el RPE o la nota de este paso.",
    done: "Hecho",
    editRepeat: "Editar repetición",
    editRepeatDescription: "Cambia cuántas veces se repite este bloque.",
    repsDecrease: "Una repetición menos",
    repsIncrease: "Una repetición más",
    shortcutsHint:
      "⌥↑ ⌥↓ reordenan un paso · Intro en un valor pasa al siguiente campo · ⌘Z deshace.",
  },

  workoutProfile: {
    ariaLabel:
      "Perfil del entrenamiento, {n} pasos. El ancho de la barra es su parte del entrenamiento, la altura es el RPE.",
    noRpe: "sin RPE",
  },

  intensityInput: {
    groupLabel: "Intensidad RPE (1–10)",
    rpeValue: "RPE {n}",
  },

  workType: {
    warmUp: "Calentamiento",
    main: "Principal",
    recover: "Recuperación",
    rest: "Descanso",
    coolDown: "Vuelta a la calma",
    other: "Otro",
    work: "Trabajo",
    repeat: "repetición",
    repeats: "repeticiones",
    emptyGroup: "Grupo vacío",
    unknownStep: "(paso desconocido)",
  },

  workoutType: {
    run: "Correr",
    bike: "Bici",
    swim: "Natación",
    strength: "Fuerza",
    other: "Otro",
    workout: "Entrenamiento",
  },

  errors: {
    generic: "Algo ha salido mal. Inténtalo de nuevo.",
    INVALID_CREDENTIALS: "Email o contraseña incorrectos.",
    USER_EMAIL_ALREADY_EXISTS: "Ese email ya está registrado.",
    INVALID_RESET_TOKEN: "Este enlace no es válido o ha caducado.",
    INVALID_PASSWORD: "La contraseña debe tener al menos 8 caracteres.",
    FORBIDDEN: "No tienes permiso para hacer eso.",
    MEMBER_ALREADY_IN_TEAM: "Esa persona ya es miembro de este equipo.",
    TEAM_ALREADY_EXISTS: "Ya existe un equipo con ese nombre.",
    EMPTY_TEAM_NAME: "El nombre del equipo no puede estar vacío.",
    EMPTY_GROUP_NAME: "El nombre del grupo no puede estar vacío.",
    GROUP_HAS_WORKOUTS:
      "Este grupo todavía tiene entrenamientos. Muévelos a otro grupo o elimínalos primero.",
    LAST_GROUP: "Un equipo necesita al menos un grupo.",
    GROUP_ALREADY_EXISTS: "Ya existe un grupo con ese nombre.",
    PLATFORM_ADMIN_FORBIDDEN:
      "No tienes permiso para realizar acciones de administración de la plataforma.",
    INVALID_LOCALE: "El idioma debe ser español o inglés.",
    INVALID_WORKOUT_PLAN: "El plan del entrenamiento no es válido.",
    MISSING_STEPS: "Se requiere al menos un paso.",
    MISSING_REPS: "El número de repeticiones es obligatorio.",
    INVALID_MEASURE: "Medida no válida.",
    INVALID_MEASURE_UNIT: "Unidad de medida desconocida.",
    INVALID_MEASURE_VALUE: "Valor de medida no válido.",
    UNKNOWN_WORKOUT_TYPE: "Tipo de entrenamiento desconocido.",
    UNKNOWN_WORK_TYPE: "Tipo de trabajo desconocido.",
  },
};

export default es;
